/**
 * @fileoverview Unified permission interface for components.
 *
 * Provides permission checks, UI visibility flags, and direct access to
 * permission data. Use this hook as the primary interface for permission
 * needs in components.
 */

import type { Containment } from "models";
import { useCallback, useMemo } from "react";
import { UseCurrentUserPermissions } from "./useCurrentUserPermissions";

/**
 * @returns Permission interface with check functions, visibility flags, and
 *     data access.
 *
 * @example
 * const permissions = UseHasPermission();
 * if (permissions.hasPermission(containment, OBJECT_ID_CREATE_CONTAINMENT)) { ... }
 * if (permissions.canViewSectorAdmins) { ... }
 */
export function UseHasPermission() {
	const currentUserPermissions = UseCurrentUserPermissions();
	const hasPermissionForAllPlants = currentUserPermissions.hasPermissionForAllPlants;

	const hasPermissionForContainment = useCallback(
		(containment: Containment, objectId: string): boolean => {
			const impactedPlantIds = containment.Plants.map((plant) => plant.id);
			return hasPermissionForAllPlants(
				impactedPlantIds,
				objectId
			);
		},
		[hasPermissionForAllPlants]
	);

	const hasPermissionForPlants = useCallback(
		(plantIds: string[], objectId: string, _sectorName?: string): boolean => {
			return hasPermissionForAllPlants(plantIds, objectId);
		},
		[hasPermissionForAllPlants]
	);

	const canViewSectorAdmins = currentUserPermissions.isSectorAdmin;
	const canViewPlantUsers =
		currentUserPermissions.isSectorAdmin || currentUserPermissions.isPlantUsersAccessManager;

	return useMemo(
		() => ({
			hasPermissionForContainment,
			hasPermissionForPlants,
			hasPermission: hasPermissionForContainment,
		canViewSectorAdmins,
		canViewPlantUsers,
		isSectorAdmin: currentUserPermissions.isSectorAdmin,
		isPlantUsersAccessManager: currentUserPermissions.isPlantUsersAccessManager,
		allAccessibleSectorNames: currentUserPermissions.allAccessibleSectorNames,
		accessibleSectorIdsAsAdminSet:
			currentUserPermissions.accessibleSectorIdsAsAdminSet,
		getAccessibleSectorsForObject:
			currentUserPermissions.getAccessibleSectorsForObject,
		manageablePlants: currentUserPermissions.manageablePlants,
			createContainmentPlantIds:
				currentUserPermissions.createContainmentPlantIds,
			createContainmentSectorNames:
				currentUserPermissions.createContainmentSectorNames,
			asrsLockPlantIds: currentUserPermissions.asrsLockPlantIds,
			asrsUnlockPlantIds: currentUserPermissions.asrsUnlockPlantIds,
			getPlantIdsByObjectAndSector:
				currentUserPermissions.getPlantIdsByObjectAndSector,
		}),
		[
			hasPermissionForContainment,
			hasPermissionForPlants,
			canViewSectorAdmins,
			canViewPlantUsers,
			currentUserPermissions,
		]
	);
}